import { Image as ImageIcon } from "lucide-react";
import { StudioPageLayout } from "@/components/flowly/StudioPageLayout/StudioPageLayout";
import { ProfilePhotoHero } from "@/components/flowly/ProfilePhotoHero/ProfilePhotoHero";
import { ProfilePhotoGenderPicker } from "@/components/flowly/ProfilePhotoGenderPicker/ProfilePhotoGenderPicker";
import { ProfilePhotoPresetGrid } from "@/components/flowly/ProfilePhotoPresetGrid/ProfilePhotoPresetGrid";
import { ProfilePhotoHairPicker } from "@/components/flowly/ProfilePhotoHairPicker/ProfilePhotoHairPicker";
import { ProfilePhotoCustomStyleModal } from "@/components/flowly/ProfilePhotoCustomStyleModal/ProfilePhotoCustomStyleModal";
import { GenerationBar } from "@/components/flowly/GenerationBar/GenerationBar";
import { CreationsGallery } from "@/components/flowly/CreationsGallery/CreationsGallery";
import { MediaGridItem } from "@/components/flowly/MediaGridItem/MediaGridItem";
import { ImageLightbox } from "@/components/flowly/Lightbox/ImageLightbox/ImageLightbox";
import { ProfilePhotoUploadGuide } from "@/components/flowly/ProfilePhotoUploadGuide/ProfilePhotoUploadGuide";
import { FlowReadyCta } from "@/components/flowly/FlowReadyCta/FlowReadyCta";
import { PROFILE_PHOTO_FLOW_CREDIT_COST } from "@shared/schemas/api";
import { PROFILE_PHOTO_MIN_UPLOADS } from "@/config/profilePhotoPresets";
import { downloadMedia } from "@/lib/mediaUtils";
import { useProfilePhoto } from "./useProfilePhoto";
import {
  PROFILE_PHOTO_CONFIG_ANCHOR_ID,
  PROFILE_PHOTO_PRESETS_ANCHOR_ID,
  PROFILE_PHOTO_HAIR_ANCHOR_ID,
} from "./constants";

export default function ProfilePhotoPage() {
  const {
    gender,
    presetId,
    uploads,
    aspectRatio,
    isUploading,
    isGenerating,
    results,
    lastShowcase,
    presetsForGender,
    availableAspectRatios,
    dbImages,
    dbPendingImages,
    pendingCount,
    imageCompletingIds,
    failedImages,
    hairMode,
    customHair,
    isCustomModalOpen,
    customBackground,
    customClothing,
    showUploadGuide,
    closeUploadGuide,
    handleGenderChange,
    handlePresetSelect,
    handleAddFiles,
    handleRemoveUpload,
    handleHairModeChange,
    handleCustomHairChange,
    handleCustomBackgroundChange,
    handleCustomClothingChange,
    handleCustomModalContinue,
    handleCustomModalClose,
    setAspectRatio,
    handleGenerate,
    handleClearFailed,
    selectedMedia,
    handleOpenLightbox,
    handleCloseLightbox,
    handleDownload,
    handleShare,
    handleDelete,
    currentMediaIndex,
    canNavigate,
    goToNextItem,
    goToPrevItem,
    canGenerate,
    t,
  } = useProfilePhoto();

  // No model picker, no resolution picker: one click generates one image per
  // model (3 images) at 1k, for a flat flow price. The server charges the same
  // PROFILE_PHOTO_FLOW_CREDIT_COST it displays here.
  const creditCost = PROFILE_PHOTO_FLOW_CREDIT_COST;
  // The config steps (gender → style → hair) only unlock once the minimum
  // photos are in, so a 1-photo user isn't led to a dead-ended Create button.
  const hasMinUploads = uploads.length >= PROFILE_PHOTO_MIN_UPLOADS;

  return (
    <StudioPageLayout
      activeTab="images"
      activeApp="profilePhoto"
      footer={
        !selectedMedia ? (
          <GenerationBar
            onGenerate={handleGenerate}
            generating={isGenerating}
            disabled={!canGenerate}
            creditCost={creditCost}
            buttonLabel={t.profilePhoto.generateBtn}
            aspectRatio={aspectRatio}
            onAspectRatioChange={setAspectRatio}
            availableAspectRatios={availableAspectRatios}
          />
        ) : undefined
      }
      lightbox={
        selectedMedia ? (
          <ImageLightbox
            imageUrl={selectedMedia.image_url ?? selectedMedia.displayUrl}
            imageId={selectedMedia.id}
            referenceImageUrl={selectedMedia.reference_image_url ?? undefined}
            createdAt={selectedMedia.created_at}
            resolution={selectedMedia.resolution}
            aspectRatio={selectedMedia.aspect_ratio}
            pipeline={selectedMedia.pipeline}
            prompt={selectedMedia.prompt}
            model={selectedMedia.model}
            onClose={handleCloseLightbox}
            onRegenerate={() => {
              handleCloseLightbox();
              handleGenerate();
            }}
            onDownload={handleDownload}
            onShare={handleShare}
            onDelete={handleDelete}
            navigation={
              dbImages.length > 1
                ? {
                    currentIndex: currentMediaIndex,
                    totalItems: dbImages.length,
                    canGoNext: canNavigate,
                    canGoPrev: canNavigate,
                    onNext: goToNextItem,
                    onPrev: goToPrevItem,
                  }
                : undefined
            }
          />
        ) : undefined
      }
    >
      <div className="space-y-6 md:space-y-8 mt-2 md:mt-4">
        <ProfilePhotoUploadGuide isOpen={showUploadGuide} onClose={closeUploadGuide} />

        <ProfilePhotoCustomStyleModal
          isOpen={isCustomModalOpen}
          background={customBackground}
          clothing={customClothing}
          onBackgroundChange={handleCustomBackgroundChange}
          onClothingChange={handleCustomClothingChange}
          onContinue={() => {
            handleCustomModalContinue();
            requestAnimationFrame(() => {
              document
                .getElementById(PROFILE_PHOTO_HAIR_ANCHOR_ID)
                ?.scrollIntoView({ behavior: "smooth", block: "center" });
            });
          }}
          onClose={handleCustomModalClose}
        />

        <ProfilePhotoHero
          uploads={uploads}
          results={results}
          lastShowcase={lastShowcase}
          isUploading={isUploading}
          canGenerate={canGenerate}
          onAddFiles={handleAddFiles}
          onRemove={handleRemoveUpload}
          onGenerate={handleGenerate}
        />

        {hasMinUploads && (
          <div
            id={PROFILE_PHOTO_CONFIG_ANCHOR_ID}
            className="scroll-mt-24 space-y-6 md:space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300"
          >
            <div className="rounded-2xl border border-brand/30 bg-brand/[0.04] p-4 md:p-5">
              <ProfilePhotoGenderPicker value={gender} onChange={handleGenderChange} />
            </div>

            {gender && (
              <div
                id={PROFILE_PHOTO_PRESETS_ANCHOR_ID}
                className="scroll-mt-24 rounded-2xl border border-brand/30 bg-brand/[0.04] p-4 md:p-5"
              >
                <ProfilePhotoPresetGrid
                  presets={presetsForGender}
                  selectedId={presetId}
                  onSelect={handlePresetSelect}
                />
              </div>
            )}

            {presetId && (
              <div
                id={PROFILE_PHOTO_HAIR_ANCHOR_ID}
                className="scroll-mt-24 rounded-2xl border border-brand/30 bg-brand/[0.04] p-4 md:p-5 animate-in fade-in slide-in-from-bottom-2 duration-300"
              >
                <ProfilePhotoHairPicker
                  mode={hairMode}
                  customHair={customHair}
                  onModeChange={handleHairModeChange}
                  onCustomHairChange={handleCustomHairChange}
                />
              </div>
            )}

            {/* Inline "all set" CTA — same generate action as the footer bar.
                Mounted through the generation too so it doesn't flicker. */}
            {(canGenerate || isGenerating) && (
              <FlowReadyCta
                onGenerate={handleGenerate}
                generating={isGenerating}
                disabled={!canGenerate}
                creditCost={creditCost}
                buttonLabel={t.profilePhoto.generateBtn}
              />
            )}
          </div>
        )}

        <CreationsGallery
          title={t.profilePhoto.results}
          pendingCount={pendingCount}
          pendingLabel={t.generate.processing}
          pendingItems={dbPendingImages.map((img) => ({ id: img.id, createdAt: img.created_at }))}
          pendingMediaType="image"
          completingIds={imageCompletingIds}
          failedItems={failedImages}
          failedLabel={t.generate.failedRefunded}
          onClearFailed={handleClearFailed}
          className="mt-2 mb-4"
          emptyIcon={ImageIcon}
          emptyTitle={t.profilePhoto.noResults}
        >
          {dbImages.map((img, idx) => {
            const url = img.image_url ?? img.displayUrl;
            return (
              <MediaGridItem
                key={img.id}
                imageUrl={img.displayUrl}
                hoverText={t.generate.clickToOpen}
                alt={`${t.profilePhoto.results} ${idx + 1}`}
                onClick={() => handleOpenLightbox(img)}
                onDownload={
                  url ? () => downloadMedia(url, "profile-photo", "png") : undefined
                }
                downloadLabel={t.profilePhoto.downloadAria}
              />
            );
          })}
        </CreationsGallery>

      </div>
    </StudioPageLayout>
  );
}
